import { AxiosError } from "axios";
import { Button, Container, Link, List, Message, Stack, Text } from "basis";
import { Otp } from "components/Otp/Otp";
import { putHref } from "library/api";
import { FullAddress } from "library/api/address/types.generated";
import { OtpRequiredErrorType } from "library/api/otp/otp";
import {
  Action,
  ActionType,
  ReportLostStolenOrDamagedPayload,
} from "pages/LostStolenOrDamaged/types";
import { Address } from "pages/ManagePersonalDetails/components/Address";
import { Dispatch, useState } from "react";
import { useMutation } from "react-query";

interface Props {
  address: FullAddress;
  cardEndsWith: string;
  lostStolenDamagedUrl: string;
  dispatch: Dispatch<Action>;
  token: string;
}

export const DamagedConfirmation = ({
  address,
  cardEndsWith,
  lostStolenDamagedUrl,
  dispatch,
  token,
}: Props) => {
  const [clientError, setClientError] = useState<boolean>(false);

  const [otpState, setOtpState] = useState<{
    createSessionUrl: string;
    token: string;
  } | null>(null);

  const reportDamagedConfirmation = useMutation<
    void,
    AxiosError | AxiosError<OtpRequiredErrorType>,
    void
  >(
    async () => {
      await putHref<void, ReportLostStolenOrDamagedPayload>(
        lostStolenDamagedUrl,
        {
          request_type: "REPLACE",
        },
      );
    },
    {
      onSuccess() {
        dispatch({ type: ActionType.SUCCESS });
      },
      onError(error) {
        if (error.response?.data.type === "2FA_REQUIRED") {
          const createSessionUrl = error.response?.data.links.find(
            (link) => link.rel === "sessions",
          )?.href;

          if (!createSessionUrl) {
            console.error("Missing Create Session Url from payload");
            dispatch({ type: ActionType.ERROR });
            return;
          }

          setOtpState({
            token,
            createSessionUrl,
          });
          return;
        }

        if (String(error.response?.status).startsWith("4")) {
          setClientError(true);
          return;
        }

        dispatch({ type: ActionType.ERROR });
      },
    },
  );

  if (otpState) {
    return (
      <Otp
        createSessionUrl={otpState.createSessionUrl}
        token={otpState.token}
        onSuccess={() => {
          reportDamagedConfirmation.mutate();
        }}
        onBackClick={() => {
          setOtpState(null);
        }}
      />
    );
  }

  return (
    <Stack gap="8">
      <Text textStyle="heading4">Confirm your mailing address</Text>
      <Text>
        We’ll cancel your card ending {cardEndsWith} and send a replacement for
        your card to the address below.
      </Text>
      <Text textStyle="heading5" color="primary.blue.t100">
        Mailing Address
      </Text>
      <Address address={address} />
      <Message
        severity="info-or-minor"
        bg="secondary.lightBlue.t25"
        title="Have a different mailing address?"
      >
        If your mailing address above is incorrect, please{" "}
        <Link href="/manage-personal-details">update your details</Link> before
        proceeding to report your card lost or stolen.
      </Message>
      <Container hasBorder padding="6 8">
        <Stack gap={4}>
          <Text>Important things you need to know</Text>
          <List>
            <List.Item>
              Activate your replacement card when you receive it.
            </List.Item>
            <List.Item>
              Destroy your damaged card by cutting it in half, diagonally
              through the magnetic strip and chip.
            </List.Item>
            <List.Item>
              Your replacement card will have the same card number but a new
              expiry date and CVV number.
            </List.Item>
            <List.Item>
              If you have set up any recurring or automatic payments from this
              card, you'll need to update them with the new card details.
            </List.Item>
            <List.Item>
              If you have temporarily locked your card ending {cardEndsWith},
              the lock will be applied to your replacement card.
            </List.Item>
            <List.Item>
              Payments into your account will not be affected.
            </List.Item>
          </List>
        </Stack>
      </Container>

      {clientError && (
        <Message severity="info-or-minor" bg="secondary.pink.t30">
          We’ve encountered an unexpected problem and are experiencing
          difficulties in processing your request.
          <br />
          Please try again or contact Latitude immediately to report your card.
        </Message>
      )}

      <Button
        onClick={() => {
          reportDamagedConfirmation.mutate();
        }}
      >
        Report damaged card
      </Button>
    </Stack>
  );
};
